import { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { supabase } from '../services/supabase'
import Card from './Card'
import Button from './Button'
import Input from './Input'
import Textarea from './Textarea'
import Select from './Select'
import { useToast } from '../contexts/ToastContext'

const DRAFT_STORAGE_KEY = 'org_request_wizard_draft'
import {
  Building2,
  Users,
  Phone,
  Mail,
  DollarSign,
  Calendar,
  MessageSquare,
  CheckCircle2,
  ChevronRight,
  ChevronLeft,
  X,
} from 'lucide-react'

const PAIN_POINTS_OPTIONS = [
  'Paper-based records are inefficient',
  'Data scattered across multiple systems',
  'Difficulty tracking client progress',
  'Time-consuming documentation',
  'Lack of analytics and insights',
  'Poor client communication',
  'Staff coordination challenges',
  'Compliance and reporting difficulties',
  'Limited remote access',
  'No standardized workflows',
]

const EXPECTATIONS_OPTIONS = [
  'Streamlined documentation',
  'Better client outcomes tracking',
  'Improved team collaboration',
  'Data-driven insights',
  'Mobile accessibility',
  'Automated reminders',
  'Customizable workflows',
  'Integration capabilities',
  'Professional reporting',
  'Scalable solution',
]

const DESIRED_FEATURES_OPTIONS = [
  'Client management',
  'Examination templates',
  'Care plan tracking',
  'Therapy session management',
  'Vital signs monitoring',
  'Document management',
  'Analytics dashboard',
  'Mobile app',
  'API access',
  'Custom branding',
]

const RequestOrganizationWizard = ({ onSuccess, onCancel }) => {
  const { success, error: showError } = useToast()
  
  // Initialize form data - try to load from localStorage first
  const getInitialFormData = () => {
    try {
      const saved = localStorage.getItem(DRAFT_STORAGE_KEY)
      if (saved) {
        const parsed = JSON.parse(saved)
        return parsed.formData || getDefaultFormData()
      }
    } catch (error) {
      console.error('Error loading draft:', error)
    }
    return getDefaultFormData()
  }

  const getDefaultFormData = () => ({
    // Organization Details
    requested_organization_name: '',
    organization_type: '',
    organization_description: '',
    expected_number_of_users: '',
    expected_number_of_clients: '',
    
    // Contact Information
    contact_email: '',
    contact_phone: '',
    contact_name: '',
    preferred_contact_method: 'email',
    timezone: '',
    
    // Marketing & UX
    current_pain_points: [],
    main_challenges: '',
    expectations: [],
    desired_features: [],
    current_solutions: '',
    budget_range: '',
    timeline: '',
    
    // UX Feedback
    how_did_you_hear_about_us: '',
    what_made_you_interested: '',
    additional_notes: '',
  })

  // Initialize step - try to load from localStorage first
  const getInitialStep = () => {
    try {
      const saved = localStorage.getItem(DRAFT_STORAGE_KEY)
      if (saved) {
        const parsed = JSON.parse(saved)
        return parsed.currentStep || 1
      }
    } catch (error) {
      console.error('Error loading draft:', error)
    }
    return 1
  }

  const [currentStep, setCurrentStep] = useState(getInitialStep)
  const [submitting, setSubmitting] = useState(false)
  const [formData, setFormData] = useState(getInitialFormData)
  const autoSaveTimeoutRef = useRef(null)

  const totalSteps = 4

  // Load draft on mount
  useEffect(() => {
    const saved = localStorage.getItem(DRAFT_STORAGE_KEY)
    if (saved) {
      try {
        const parsed = JSON.parse(saved)
        if (parsed.formData) {
          setFormData(parsed.formData)
        }
        if (parsed.currentStep) {
          setCurrentStep(parsed.currentStep)
        }
        // Show a subtle notification that draft was restored
        if (parsed.savedAt) {
          // Use setTimeout to avoid calling during render
          setTimeout(() => {
            success('Your previous progress has been restored. You can continue where you left off.', {
              title: 'Draft Restored',
              duration: 4000,
            })
          }, 100)
        }
      } catch (error) {
        console.error('Error parsing saved draft:', error)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Save draft to localStorage
  const saveDraft = () => {
    try {
      const draft = {
        formData,
        currentStep,
        savedAt: new Date().toISOString(),
      }
      localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draft))
    } catch (error) {
      console.error('Error saving draft:', error)
    }
  }

  // Clear draft from localStorage
  const clearDraft = () => {
    try {
      localStorage.removeItem(DRAFT_STORAGE_KEY)
      // Clear any pending auto-save
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current)
        autoSaveTimeoutRef.current = null
      }
    } catch (error) {
      console.error('Error clearing draft:', error)
    }
  }

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current)
      }
    }
  }, [])

  const updateFormData = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value }
      // Auto-save on field update (debounced)
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current)
      }
      autoSaveTimeoutRef.current = setTimeout(() => {
        try {
          const draft = {
            formData: updated,
            currentStep,
            savedAt: new Date().toISOString(),
          }
          localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draft))
        } catch (error) {
          console.error('Error auto-saving draft:', error)
        }
      }, 500) // Debounce by 500ms
      return updated
    })
  }

  const toggleArrayField = (field, value) => {
    setFormData(prev => {
      const updated = {
        ...prev,
        [field]: prev[field].includes(value)
          ? prev[field].filter(item => item !== value)
          : [...prev[field], value]
      }
      // Auto-save on field update (debounced)
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current)
      }
      autoSaveTimeoutRef.current = setTimeout(() => {
        try {
          const draft = {
            formData: updated,
            currentStep,
            savedAt: new Date().toISOString(),
          }
          localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draft))
        } catch (error) {
          console.error('Error auto-saving draft:', error)
        }
      }, 500) // Debounce by 500ms
      return updated
    })
  }

  const handleSubmit = async () => {
    try {
      setSubmitting(true)
      
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) {
        throw new Error('User not authenticated')
      }

      const { data, error } = await supabase
        .from('organization_requests')
        .insert({
          user_id: user.id,
          user_email: user.email,
          user_name: user.user_metadata?.name || user.email.split('@')[0],
          ...formData,
          expected_number_of_users: formData.expected_number_of_users ? parseInt(formData.expected_number_of_users) : null,
          expected_number_of_clients: formData.expected_number_of_clients ? parseInt(formData.expected_number_of_clients) : null,
          current_pain_points: formData.current_pain_points,
          expectations: formData.expectations,
          desired_features: formData.desired_features,
        })
        .select()
        .single()

      if (error) throw error

      // Clear draft after successful submission
      clearDraft()

      success('Your organization request has been submitted successfully! Our team will review it and contact you soon.', {
        title: 'Request Submitted',
        duration: 6000,
      })

      if (onSuccess) onSuccess(data)
    } catch (error) {
      console.error('Error submitting request:', error)
      showError(error.message || 'Failed to submit request. Please try again.', {
        title: 'Submission Failed',
      })
    } finally {
      setSubmitting(false)
    }
  }

  const validateStep = (step) => {
    switch (step) {
      case 1:
        return formData.requested_organization_name && formData.organization_type
      case 2:
        return formData.contact_email && formData.contact_name
      case 3:
        return formData.budget_range && formData.timeline
      case 4:
        return true // Optional fields
      default:
        return true
    }
  }

  const nextStep = () => {
    if (validateStep(currentStep) && currentStep < totalSteps) {
      const nextStepNum = currentStep + 1
      setCurrentStep(nextStepNum)
      // Save draft when moving to next step
      try {
        const draft = {
          formData,
          currentStep: nextStepNum,
          savedAt: new Date().toISOString(),
        }
        localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draft))
      } catch (error) {
        console.error('Error saving draft on next step:', error)
      }
    }
  }

  const prevStep = () => {
    if (currentStep > 1) {
      const prevStepNum = currentStep - 1
      setCurrentStep(prevStepNum)
      // Save draft when moving to previous step (in case user goes back and forth)
      try {
        const draft = {
          formData,
          currentStep: prevStepNum,
          savedAt: new Date().toISOString(),
        }
        localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draft))
      } catch (error) {
        console.error('Error saving draft on prev step:', error)
      }
    }
  }

  return (
    <div className="space-y-6">
      {/* Progress Indicator */}
      <div className="flex items-center justify-between mb-6">
        {[...Array(totalSteps)].map((_, i) => (
          <div key={i} className="flex items-center flex-1">
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                i + 1 <= currentStep
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
              }`}
            >
              {i + 1 < currentStep ? <CheckCircle2 size={20} /> : i + 1}
            </div>
            {i < totalSteps - 1 && (
              <div
                className={`flex-1 h-1 mx-2 ${
                  i + 1 < currentStep ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-700'
                }`}
              />
            )}
          </div>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.3 }}
        >
          {/* Step 1: Organization Details */}
          {currentStep === 1 && (
            <div className="space-y-4">
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
                <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                  <Building2 size={20} />
                  Organization Details
                </h3>
                <p className="text-sm text-blue-800">
                  Tell us about the organization you'd like to set up.
                </p>
              </div>

              <Input
                label="Organization Name"
                placeholder="e.g. Wellness Center of Hope"
                value={formData.requested_organization_name}
                onChange={(e) => updateFormData('requested_organization_name', e.target.value)}
                required
              />

              <Select
                label="Organization Type"
                options={[
                  { label: 'Wellness Center', value: 'wellness_center' },
                  { label: 'Clinic', value: 'clinic' },
                  { label: 'Hospital', value: 'hospital' },
                  { label: 'Ministry', value: 'ministry' },
                  { label: 'Other', value: 'other' },
                ]}
                value={formData.organization_type}
                onChange={(e) => updateFormData('organization_type', e.target.value)}
                required
              />

              <Textarea
                label="Organization Description"
                placeholder="Brief description of your organization and its mission..."
                value={formData.organization_description}
                onChange={(e) => updateFormData('organization_description', e.target.value)}
                rows={4}
              />

              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Expected Number of Users"
                  type="number"
                  placeholder="e.g. 10"
                  value={formData.expected_number_of_users}
                  onChange={(e) => updateFormData('expected_number_of_users', e.target.value)}
                />
                <Input
                  label="Expected Number of Clients"
                  type="number"
                  placeholder="e.g. 100"
                  value={formData.expected_number_of_clients}
                  onChange={(e) => updateFormData('expected_number_of_clients', e.target.value)}
                />
              </div>
            </div>
          )}

          {/* Step 2: Contact Information */}
          {currentStep === 2 && (
            <div className="space-y-4">
              <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
                <h3 className="font-semibold text-green-900 mb-2 flex items-center gap-2">
                  <Phone size={20} />
                  Contact Information
                </h3>
                <p className="text-sm text-green-800">
                  How can we reach you to discuss your request?
                </p>
              </div>

              <Input
                label="Contact Name"
                placeholder="Your full name"
                value={formData.contact_name}
                onChange={(e) => updateFormData('contact_name', e.target.value)}
                required
              />

              <Input
                label="Contact Email"
                type="email"
                placeholder="your.email@example.com"
                value={formData.contact_email}
                onChange={(e) => updateFormData('contact_email', e.target.value)}
                required
              />

              <Input
                label="Contact Phone"
                type="tel"
                placeholder="+1 (555) 123-4567"
                value={formData.contact_phone}
                onChange={(e) => updateFormData('contact_phone', e.target.value)}
              />

              <Select
                label="Preferred Contact Method"
                options={[
                  { label: 'Email', value: 'email' },
                  { label: 'Phone', value: 'phone' },
                  { label: 'Both', value: 'both' },
                ]}
                value={formData.preferred_contact_method}
                onChange={(e) => updateFormData('preferred_contact_method', e.target.value)}
              />

              <Input
                label="Timezone"
                placeholder="e.g. America/New_York"
                value={formData.timezone}
                onChange={(e) => updateFormData('timezone', e.target.value)}
              />
            </div>
          )}

          {/* Step 3: Business & Planning */}
          {currentStep === 3 && (
            <div className="space-y-4">
              <div className="p-4 bg-purple-50 border border-purple-200 rounded-xl">
                <h3 className="font-semibold text-purple-900 mb-2 flex items-center gap-2">
                  <DollarSign size={20} />
                  Business & Planning
                </h3>
                <p className="text-sm text-purple-800">
                  Help us understand your needs and timeline.
                </p>
              </div>

              <Select
                label="Budget Range (Monthly)"
                options={[
                  { label: 'Under $50', value: 'under_50' },
                  { label: '$50 - $150', value: '50_150' },
                  { label: '$150 - $300', value: '150_300' },
                  { label: '$300+', value: '300_plus' },
                  { label: 'Custom/Enterprise', value: 'custom' },
                ]}
                value={formData.budget_range}
                onChange={(e) => updateFormData('budget_range', e.target.value)}
                required
              />

              <Select
                label="Timeline"
                options={[
                  { label: 'Immediate', value: 'immediate' },
                  { label: 'Within 1 Month', value: '1_month' },
                  { label: 'Within 3 Months', value: '3_months' },
                  { label: 'Within 6 Months', value: '6_months' },
                  { label: 'Flexible', value: 'flexible' },
                ]}
                value={formData.timeline}
                onChange={(e) => updateFormData('timeline', e.target.value)}
                required
              />

              <Textarea
                label="Current Solutions"
                placeholder="What are you currently using? (e.g., paper records, Excel, other software)"
                value={formData.current_solutions}
                onChange={(e) => updateFormData('current_solutions', e.target.value)}
                rows={3}
              />

              <Textarea
                label="Main Challenges"
                placeholder="What are your biggest challenges with your current system?"
                value={formData.main_challenges}
                onChange={(e) => updateFormData('main_challenges', e.target.value)}
                rows={4}
              />
            </div>
          )}

          {/* Step 4: Marketing & UX Research */}
          {currentStep === 4 && (
            <div className="space-y-4">
              <div className="p-4 bg-orange-50 border border-orange-200 rounded-xl">
                <h3 className="font-semibold text-orange-900 mb-2 flex items-center gap-2">
                  <MessageSquare size={20} />
                  Help Us Improve
                </h3>
                <p className="text-sm text-orange-800">
                  Your feedback helps us build better solutions. All fields are optional.
                </p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Current Pain Points (Select all that apply)
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {PAIN_POINTS_OPTIONS.map((point) => (
                    <label
                      key={point}
                      className="flex items-center gap-2 p-2 border rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 dark:border-gray-700"
                    >
                      <input
                        type="checkbox"
                        checked={formData.current_pain_points.includes(point)}
                        onChange={() => toggleArrayField('current_pain_points', point)}
                        className="rounded"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">{point}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Expectations (Select all that apply)
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {EXPECTATIONS_OPTIONS.map((expectation) => (
                    <label
                      key={expectation}
                      className="flex items-center gap-2 p-2 border rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 dark:border-gray-700"
                    >
                      <input
                        type="checkbox"
                        checked={formData.expectations.includes(expectation)}
                        onChange={() => toggleArrayField('expectations', expectation)}
                        className="rounded"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">{expectation}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Desired Features (Select all that apply)
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {DESIRED_FEATURES_OPTIONS.map((feature) => (
                    <label
                      key={feature}
                      className="flex items-center gap-2 p-2 border rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 dark:border-gray-700"
                    >
                      <input
                        type="checkbox"
                        checked={formData.desired_features.includes(feature)}
                        onChange={() => toggleArrayField('desired_features', feature)}
                        className="rounded"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">{feature}</span>
                    </label>
                  ))}
                </div>
              </div>

              <Input
                label="How did you hear about us?"
                placeholder="e.g., Google search, referral, social media..."
                value={formData.how_did_you_hear_about_us}
                onChange={(e) => updateFormData('how_did_you_hear_about_us', e.target.value)}
              />

              <Textarea
                label="What made you interested in our solution?"
                placeholder="Tell us what caught your attention..."
                value={formData.what_made_you_interested}
                onChange={(e) => updateFormData('what_made_you_interested', e.target.value)}
                rows={3}
              />

              <Textarea
                label="Additional Notes"
                placeholder="Anything else you'd like us to know?"
                value={formData.additional_notes}
                onChange={(e) => updateFormData('additional_notes', e.target.value)}
                rows={3}
              />
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      {/* Navigation Buttons */}
      <div className="flex justify-between pt-6 border-t">
        <div>
          {currentStep > 1 && (
            <Button variant="secondary" onClick={prevStep}>
              <ChevronLeft size={18} className="mr-2" />
              Previous
            </Button>
          )}
        </div>
        <div className="flex gap-3">
          {onCancel && (
            <Button 
              variant="secondary" 
              onClick={() => {
                // Clear draft when canceling
                clearDraft()
                onCancel()
              }}
            >
              Cancel
            </Button>
          )}
          {currentStep < totalSteps ? (
            <Button onClick={nextStep} disabled={!validateStep(currentStep)}>
              Next
              <ChevronRight size={18} className="ml-2" />
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={submitting || !validateStep(currentStep)}>
              {submitting ? 'Submitting...' : 'Submit Request'}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

export default RequestOrganizationWizard

